import React, { useState, useEffect, useCallback } from 'react';
import { linkifyText } from '../../utils/linkUtils';
import {
  Box,
  Card,

  Typography,
  Switch,
  Grid,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  useTheme,
  Chip,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  IconButton,
  FormControl,
  InputLabel,
  Select,
  MenuItem,
  FormHelperText,
  Avatar,

  Stack,
  InputAdornment,
  Collapse,
  Alert,
  Tooltip,
  useMediaQuery,
  Menu,
  ListItemIcon,
  ListItemText,
} from '@mui/material';
import {
  ContentCopy as CopyIcon,
  FiberManualRecord as StatusIcon,
  Add as AddIcon,
  Remove as RemoveIcon,
  Business as BusinessIcon,
  Dashboard as DashboardIcon,
  Search as SearchIcon,
  ExpandMore as ExpandMoreIcon,
  ExpandLess as ExpandLessIcon,
  AdminPanelSettings as AdminIcon,
  Settings as SettingsIcon,
  Visibility as VisibilityIcon,
  VisibilityOff as VisibilityOffIcon,
  ChevronLeft as ChevronLeftIcon,
  ChevronRight as ChevronRightIcon,
} from '@mui/icons-material';
import api from '../../utils/api';
import { useFormik } from 'formik';
import * as yup from 'yup';
import { useHeartbeat } from '../../contexts/HeartbeatContext';
import SuccessDialog from '../../components/SuccessDialog';
import './admin-css/ClientManagement-light.css';
import './admin-css/ClientManagement-dark.css';


interface Company {
  _id: string;
  companyName: string;
  companyIndustry: string;
  maxUsers: number;
  isActive: boolean;
  urlSlug?: string; // Company URL slug for company-specific URLs
  branding: {
    loginImageUrl: string;
  };
  users?: User[];
  activeUsersCount?: number;
  createdAt: string;
}

interface User {
  _id: string;
  clientName: string;
  email: string;
  username: string;
  role: 'admin' | 'editor' | 'viewer';
  isActive: boolean;
  isOnline: boolean;
  lastLogin?: string;
  lastHeartbeat?: string;
  createdAt: string;
  companyId: string;
}

interface NewCompanyFormValues {
  companyName: string;
  companyIndustry: string;
  maxUsers: number;
}

interface NewUserFormValues {
  clientName: string;
  email: string;
  username: string;
  password: string;
  role: 'admin' | 'editor' | 'viewer';
}

interface EditCompanyFormValues {
  companyName: string;
  companyIndustry: string;
  maxUsers: number;
}

const companyValidationSchema = yup.object({
  companyName: yup.string().required('Company name is required'),
  companyIndustry: yup.string().required('Company industry is required'),
  maxUsers: yup.number().min(1, 'Must allow at least 1 user').max(100, 'Maximum 100 users allowed').required('Max users is required'),
});

const userValidationSchema = yup.object({
  clientName: yup.string().required('Name is required'),
  email: yup.string().email('Enter a valid email').required('Email is required'),
  username: yup
    .string()
    .matches(/^[a-zA-Z0-9_]+$/, 'Username can only contain letters, numbers, and underscores')
    .required('Username is required'),
  password: yup
    .string()
    .min(8, 'Password should be of minimum 8 characters length')
    .required('Password is required'),
  role: yup.string().oneOf(['admin', 'editor', 'viewer'], 'Role must be admin, editor, or viewer').required('Role is required'),
});

const editCompanyValidationSchema = yup.object({
  companyName: yup.string().required('Company name is required'),
  companyIndustry: yup.string().required('Company industry is required'),
  maxUsers: yup.number().min(1, 'Must allow at least 1 user').max(1000, 'Maximum 1000 users allowed').required('Max users is required'),
});

const ClientManagement: React.FC = () => {
  const theme = useTheme();
  const { lastHeartbeat } = useHeartbeat();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  const [companies, setCompanies] = useState<Company[]>([]);
  const [loading, setLoading] = useState(true);
  const [deleteDialogOpen, setDeleteDialogOpen] = useState(false);
  const [createCompanyDialogOpen, setCreateCompanyDialogOpen] = useState(false);
  const [createUserDialogOpen, setCreateUserDialogOpen] = useState(false);
  const [editCompanyDialogOpen, setEditCompanyDialogOpen] = useState(false);
  const [selectedCompany, setSelectedCompany] = useState<Company | null>(null);
  const [selectedUser, setSelectedUser] = useState<User | null>(null);
  const [expandedRow, setExpandedRow] = useState<string | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [deleteTarget, setDeleteTarget] = useState<'company' | 'user' | null>(null);
  const [toggleConfirmDialogOpen, setToggleConfirmDialogOpen] = useState(false);
  const [toggleCompany, setToggleCompany] = useState<Company | null>(null);
  const [showUserPassword, setShowUserPassword] = useState(false);
  const [showSuccessDialog, setShowSuccessDialog] = useState(false);
  const [successDialogConfig, setSuccessDialogConfig] = useState({
    title: '',
    message: '',
    autoCloseDelay: 5000, // Default 5 seconds
  });
  
  // Mobile menu state
  const [mobileMenuAnchor, setMobileMenuAnchor] = useState<HTMLElement | null>(null);
  const [selectedCompanyForMenu, setSelectedCompanyForMenu] = useState<Company | null>(null);
  
  // Pagination state
  const [currentPage, setCurrentPage] = useState(1);
  const [rowsPerPage] = useState(6);

  const fetchCompanies = useCallback(async () => {
          try {
        const response = await api.get('/api/companies');
      
      if (response.data.success) {
        setCompanies(response.data.data);
      } else {
        setCompanies([]);
      }
    } catch (error: any) {
      console.error('Error fetching companies:', error);
      setCompanies([]);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchCompanies();
  }, [fetchCompanies]);

  // No need for JavaScript overrides - using dedicated CSS files instead

  // Check for URL parameters to open dialogs
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    if (urlParams.get('openAddUserDialog') === 'true') {
      setCreateUserDialogOpen(true);
      // Clean up the URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
    if (urlParams.get('openAddCompanyDialog') === 'true') {
      setCreateCompanyDialogOpen(true);
      // Clean up the URL parameter
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }
  }, []);

  // Auto-refresh companies every 15 seconds to match heartbeat interval (same as AdminManagement)
  useEffect(() => {
    const intervalId = setInterval(() => {
      fetchCompanies();
    }, 15000);

    return () => clearInterval(intervalId);
  }, [fetchCompanies]);

  // Refresh when heartbeat updates
  useEffect(() => {
    if (lastHeartbeat) {
      fetchCompanies();
    }
  }, [lastHeartbeat, fetchCompanies]);

  // Add window focus event to refresh immediately when admin comes back to the page
  useEffect(() => {
    const handleWindowFocus = () => {
      fetchCompanies();
    };

    window.addEventListener('focus', handleWindowFocus);
    return () => window.removeEventListener('focus', handleWindowFocus);
  }, [fetchCompanies]);

  const companyFormik = useFormik<NewCompanyFormValues>({
    initialValues: {
      companyName: '',
      companyIndustry: '',
      maxUsers: 10,
    },
    validationSchema: companyValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await api.post('/api/companies', {
          companyName: values.companyName,
          companyIndustry: values.companyIndustry,
          maxUsers: values.maxUsers,
          branding: {},
        });
        resetForm();
        setCreateCompanyDialogOpen(false);
        fetchCompanies();
        setSuccessDialogConfig({
          title: 'Company Created Successfully',
          message: `The company "${values.companyName}" has been created successfully with ${values.maxUsers} maximum users.`,
          autoCloseDelay: 5000, // Default 5 seconds
        });
        setShowSuccessDialog(true);
      } catch (error: any) {
        setSuccessDialogConfig({
          title: 'Error Creating Company',
          message: error.response?.data?.message || 'Failed to create company',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
    },
  });

  const userFormik = useFormik<NewUserFormValues>({
    initialValues: {
      clientName: '',
      email: '',
      username: '',
      password: '',
      role: 'viewer',
    },
    validationSchema: userValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await api.post(`/api/companies/${selectedCompany?._id}/users`, { ...values, role: values.role });
        resetForm();
        setCreateUserDialogOpen(false);
        fetchCompanies();
        setSuccessDialogConfig({
          title: 'User Created Successfully',
          message: `The ${values.role} user "${values.clientName}" has been created successfully for ${selectedCompany?.companyName}.`,
          autoCloseDelay: 5000, // Default 5 seconds
        });
        setShowSuccessDialog(true);
      } catch (error: any) {
        setSuccessDialogConfig({
          title: 'Error Creating User',
          message: error.response?.data?.message || 'Failed to create user',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
    },
  });

  const editCompanyFormik = useFormik<EditCompanyFormValues>({
    initialValues: {
      companyName: '',
      companyIndustry: '',
      maxUsers: 10,
    },
    validationSchema: editCompanyValidationSchema,
    onSubmit: async (values, { resetForm }) => {
      try {
        await api.put(`/api/companies/${selectedCompany?._id}`, values);
        resetForm();
        setEditCompanyDialogOpen(false);
        setSelectedCompany(null);
        fetchCompanies();
        setSuccessDialogConfig({
          title: 'Company Updated Successfully',
          message: `The company "${values.companyName}" has been updated successfully with ${values.maxUsers} maximum users.`,
          autoCloseDelay: 5000, // Default 5 seconds
        });
        setShowSuccessDialog(true);
      } catch (error: any) {
        setSuccessDialogConfig({
          title: 'Error Updating Company',
          message: error.response?.data?.message || 'Failed to update company',
          autoCloseDelay: 3000,
        });
        setShowSuccessDialog(true);
      }
    },
  });

  const handleUserToggle = async (userId: string, isActive: boolean) => {
    try {
      await api.patch(`/api/companies/users/${userId}/toggle`, { isActive: !isActive });
      fetchCompanies();
      setSuccessDialogConfig({
        title: 'User Status Updated Successfully',
        message: `The user has been ${isActive ? 'disabled' : 'enabled'} successfully.`,
        autoCloseDelay: 5000, // Default 5 seconds
      });
      setShowSuccessDialog(true);
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Updating User Status',
        message: error.response?.data?.message || 'Failed to update user status',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const handleCompanyToggle = async (companyId: string, isActive: boolean) => {
    // Optimistic update - immediately update UI
    setCompanies(prevCompanies => 
      prevCompanies.map(company => 
        company._id === companyId 
          ? { ...company, isActive: !isActive }
          : company
      )
    );

    try {
      await api.patch(`/api/companies/${companyId}/toggle`, { isActive: !isActive });
      // Refresh to get actual state from server
      fetchCompanies();
      
      // Show success dialog instead of snackbar
      const company = companies.find(c => c._id === companyId);
      setSuccessDialogConfig({
        title: !isActive ? 'Company Enabled Successfully' : 'Company Disabled Successfully',
        message: `${company?.companyName} has been ${!isActive ? 'enabled - all users auto-enabled' : 'disabled - all users auto-disabled'}!`,
        autoCloseDelay: 3000, // 3 seconds
      });
      setShowSuccessDialog(true);
    } catch (error: any) {
      // Revert on error
      setCompanies(prevCompanies => 
        prevCompanies.map(company => 
          company._id === companyId 
            ? { ...company, isActive: isActive }
            : company
        )
      );
      setSuccessDialogConfig({
        title: 'Error Updating Company Status',
        message: error.response?.data?.message || 'Failed to update company status',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const handleDelete = async () => {
    if (!selectedCompany && !selectedUser) return;
    
    try {
      if (deleteTarget === 'company' && selectedCompany) {
        await api.delete(`/api/companies/${selectedCompany._id}`);
        setSuccessDialogConfig({
          title: 'Company Deleted Successfully',
          message: `The company "${selectedCompany.companyName}" has been deleted successfully.`,
          autoCloseDelay: 5000, // Default 5 seconds
        });
        setShowSuccessDialog(true);
      } else if (deleteTarget === 'user' && selectedUser) {
        await api.delete(`/api/companies/users/${selectedUser._id}`);
        setSuccessDialogConfig({
          title: 'User Deleted Successfully',
          message: `The user "${selectedUser.clientName}" has been deleted successfully.`,
          autoCloseDelay: 5000, // Default 5 seconds
        });
        setShowSuccessDialog(true);
      }
      
      fetchCompanies();
      setDeleteDialogOpen(false);
      setSelectedCompany(null);
      setSelectedUser(null);
      setDeleteTarget(null);
    } catch (error: any) {
      setSuccessDialogConfig({
        title: 'Error Deleting',
        message: error.response?.data?.message || 'Failed to delete',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const handleCopyDashboardUrl = async (user: User) => {
    // Find the company for this user to get the company slug
    const company = companies.find(c => c.users?.some(u => u._id === user._id));
    const companySlug = company?.urlSlug;
    
    let dashboardUrl;
    if (companySlug) {
      // Use new company-specific URL structure
      dashboardUrl = `${window.location.origin}/${companySlug}/login`;
    } else {
      // Fallback to client signup if no company slug found
      dashboardUrl = `${window.location.origin}/client-signup`;
    }
    
    try {
      await navigator.clipboard.writeText(dashboardUrl);
      setSuccessDialogConfig({
        title: 'URL Copied Successfully',
        message: `Login URL for ${user.clientName} (${company?.companyName || 'Unknown Company'}) copied to clipboard!`,
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    } catch (error) {
      setSuccessDialogConfig({
        title: 'Error Copying URL',
        message: 'Failed to copy URL',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const handleAccessDashboard = async (user: User) => {
    try {
      // Generate temporary client access token
      const response = await api.post('/api/auth/admin/access-client', { clientId: user._id });
      
      if (response.data.success) {
        // Get temporary token and client info
        const tempToken = response.data.tempToken;
        const clientUsername = response.data.clientInfo.username; // Use actual client username
        
        // Note: We don't store the token in admin's sessionStorage since the new window has its own context
        
        // Open in new window to avoid disrupting admin session and prevent session conflicts
        // Get company slug from the user's company data
        const company = companies.find(c => c.users?.some(u => u._id === user._id));
        const companySlug = company?.urlSlug || response.data.clientInfo.companySlug;
        
        let dashboardUrl;
        if (companySlug) {
          // Use the dedicated super admin viewer route for better isolation
          dashboardUrl = `${window.location.origin}/${companySlug}/${clientUsername}/super-admin-viewer?temp_access=true&token=${encodeURIComponent(tempToken)}`;
        } else {
          // Fallback to existing client dashboard route
          dashboardUrl = `${window.location.origin}/client/${clientUsername}/super-admin-viewer?temp_access=true&token=${encodeURIComponent(tempToken)}`;
        }
        
        // Open in new tab for better user experience and to avoid popup blockers
        // Each tab will have its own isolated sessionStorage context
        const tabName = `client_dashboard_${Date.now()}_${Math.random().toString(36).substr(2, 9)}`;
        
        // Open in new tab with unique name to prevent conflicts
        const newTab = window.open(dashboardUrl, tabName);
        
        if (newTab) {
          // Focus the new tab
          newTab.focus();
          // Opened Super Admin Viewer in new tab
        } else {
          // Fallback: open in current tab if popup is blocked
          console.warn('Popup blocked - opening in current tab');
          window.location.href = dashboardUrl;
        }
        
        // Show success dialog like login success
        setSuccessDialogConfig({
          title: 'Accessing Dashboard',
          message: `Accessing ${response.data.clientInfo.companyName} dashboard as Super Admin Viewer (Dedicated Route)`,
          autoCloseDelay: 3000, // 3 seconds for Super Admin Viewer access
        });
        setShowSuccessDialog(true);
      }
    } catch (error: any) {
      console.error('Error accessing client dashboard:', error);
      setSuccessDialogConfig({
        title: 'Error Accessing Dashboard',
        message: 'Failed to access client dashboard',
        autoCloseDelay: 3000,
      });
      setShowSuccessDialog(true);
    }
  };

  const handleEditCompany = (company: Company) => {
    setSelectedCompany(company);
    editCompanyFormik.setValues({
      companyName: company.companyName,
      companyIndustry: company.companyIndustry,
      maxUsers: company.maxUsers,
    });
    setEditCompanyDialogOpen(true);
  };

  const handleExpandRow = (companyId: string) => {
    setExpandedRow(expandedRow === companyId ? null : companyId);
  };

  // Mobile menu handlers
  const handleMobileMenuOpen = (event: React.MouseEvent<HTMLElement>, company: Company) => {
    setMobileMenuAnchor(event.currentTarget);
    setSelectedCompanyForMenu(company);
  };

  const handleMobileMenuClose = () => {
    setMobileMenuAnchor(null);
    setSelectedCompanyForMenu(null);
  };

  const getUserOnlineStatus = (user: User) => {
    // Use the backend-calculated isOnline status (same as AdminManagement)
    // This is more reliable than frontend calculations
    return {
      isOnline: user.isOnline,
      statusText: user.isOnline ? 'Online' : 'Offline'
    };
  };

  const filteredCompanies = companies.filter(company => {
    // Search in company fields
    const companyMatch = 
      company.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.companyIndustry.toLowerCase().includes(searchQuery.toLowerCase());
    
    // Search in users within the company
    const userMatch = company.users?.some(user => 
      user.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.role.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    return companyMatch || userMatch;
  }).map(company => {
    // If search query is empty, return company as is
    if (!searchQuery.trim()) {
      return company;
    }
    
    // Check if search matches company fields
    const companyMatch = 
      company.companyName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      company.companyIndustry.toLowerCase().includes(searchQuery.toLowerCase());
    
    // If company matches, return all users
    if (companyMatch) {
      return company;
    }
    
    // If company doesn't match, filter users to only show matching ones
    const filteredUsers = company.users?.filter(user => 
      user.clientName.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.username.toLowerCase().includes(searchQuery.toLowerCase()) ||
      user.role.toLowerCase().includes(searchQuery.toLowerCase())
    );
    
    // Return company with filtered users
    return {
      ...company,
      users: filteredUsers
    };
  });

  // Pagination calculations
  const totalItems = filteredCompanies.length;
  const totalPages = Math.ceil(totalItems / rowsPerPage);
  const startIndex = (currentPage - 1) * rowsPerPage;
  const endIndex = Math.min(startIndex + rowsPerPage, totalItems);
  const paginatedCompanies = filteredCompanies.slice(startIndex, endIndex);

  const handlePreviousPage = () => {
    setCurrentPage(prev => Math.max(prev - 1, 1));
  };

  const handleNextPage = () => {
    setCurrentPage(prev => Math.min(prev + 1, totalPages));
  };

  // Reset pagination when search changes
  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery]);

  if (loading) {
    return (
      <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', height: '50vh' }}>
        <Typography>Loading companies...</Typography>
      </Box>
    );
  }

  return (
    <Box 
      className={`client-management-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
      sx={{ 
        p: 3,
        backgroundColor: theme.palette.custom.background.admin,
        minHeight: '100vh'
      }}>
      {/* Desktop Header */}
      {!isMobile && (
        <>
          <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
            <Typography 
              variant="h2" 
              className={`client-management-title ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ 
                fontWeight: 700, 
                color: '#202224',
                fontSize: '3rem', // Twice the size of h4 (1.5rem * 2)
                fontFamily: '"Nunito Sans", sans-serif'
              }}
            >
              Client Management
            </Typography>
            <Button
              variant="contained"
              className="add-client-button"
              onClick={() => setCreateCompanyDialogOpen(true)}
              sx={{
                fontWeight: 600,
                borderRadius: '16px',
                px: 2,
                py: 0.5,
                backgroundColor: `${theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1B4672'}`,
                color: '#FFFFFF',
                '&:hover': {
                  transform: 'translateY(-1px)',
                },
              }}
            >
              Add Client
            </Button>
          </Box>

          {/* Desktop Search */}
          <Box sx={{ mb: 3 }}>
            <TextField
              fullWidth
              className={`search-field ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              placeholder="Search companies, users, or roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
              }}
              sx={{ maxWidth: 400 }}
            />
          </Box>
        </>
      )}

      {/* Mobile Header */}
      {isMobile && (
        <Box className={`mobile-header ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
          {/* First Row: Title and Add Button */}
          <Box className={`mobile-header-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
            <Typography 
              variant="h5" 
              className={`client-management-title ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ 
                fontWeight: 600, 
                color: '#202224',
                fontSize: '1rem',
                fontFamily: '"Nunito Sans", sans-serif'
              }}
            >
              Client Management
            </Typography>
            
            {/* Add Client Button */}
            <Button
              variant="contained"
              size="small"
              onClick={() => setCreateCompanyDialogOpen(true)}
              sx={{
                fontWeight: 600,
                borderRadius: '16px',
                px: 3,
                py: 0.75,
                backgroundColor: `${theme.palette.mode === 'dark' ? theme.palette.primary.main : '#1B4672'}`,
                color: '#FFFFFF',
                fontSize: '0.875rem'
              }}
            >
              Add Client
            </Button>
          </Box>
          
          {/* Second Row: Full-width Search */}
          <Box className={`mobile-search-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
            <TextField
              fullWidth
              className={`search-field ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              placeholder="Search companies, users, or roles..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              size="small"
            />
          </Box>
        </Box>
      )}

      {/* Companies Table */}
      {filteredCompanies.length === 0 ? (
        <Card className={`no-companies-card ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ p: 6, textAlign: 'center' }}>
          <BusinessIcon className={`no-companies-icon ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontSize: 64, color: 'text.secondary', mb: 2 }} />
          <Typography variant="h6" color="text.secondary" gutterBottom>
            No Companies Found
          </Typography>
          <Typography variant="body1" color="text.secondary">
            Create your first company to get started.
          </Typography>
        </Card>
      ) : (
        <>
          {/* Desktop Table */}
          {!isMobile && (
            <TableContainer 
              component={Paper} 
              className={`client-table-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ borderRadius: 2, boxShadow: 2, backgroundColor: '#FFFFFF' }}
            >
              <Table className={`client-table ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                <TableHead>
                  <TableRow 
                    className={`client-table-header ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                    sx={{ backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.05)' : 'rgba(0,0,0,0.02)' }}
                  >
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Company Name</TableCell>
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Category</TableCell>
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Users</TableCell>
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', color: '#667085', fontFamily: 'Inter, sans-serif' }}>Status</TableCell>
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', textAlign: 'center' }}></TableCell>
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', textAlign: 'center' }}></TableCell>
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', textAlign: 'center' }}></TableCell>
                    <TableCell className={`client-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} sx={{ fontWeight: 'bold', fontSize: '1rem', textAlign: 'center' }}></TableCell>
                  </TableRow>
                </TableHead>
              <TableBody>
                {paginatedCompanies.map((company) => (
                  <React.Fragment key={company._id}>
                    {/* Main Company Row */}
                    <TableRow 
                      className={`client-table-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                      sx={{ 
                        '&:hover': { 
                          backgroundColor: 'transparent'
                        },
                        cursor: 'pointer'
                      }}
                      onClick={() => handleExpandRow(company._id)}
                    >
                    <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                      <Typography 
                        variant="body1" 
                        className={`table-company-name ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                        sx={{ fontWeight: 'medium' }}
                      >
                        {company.companyName}
                      </Typography>
                    </TableCell>
                    <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                      <Typography 
                        variant="body2" 
                        className={`table-company-industry ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                        color="text.secondary"
                      >
                        {company.companyIndustry}
                      </Typography>
                    </TableCell>
                    <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                      <Typography 
                        variant="body2" 
                        className={`table-users-count ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                        sx={{ fontWeight: 'medium' }}
                      >
                        {company.users?.length || 0}/{company.maxUsers} Users
                      </Typography>
                    </TableCell>
                    <TableCell className={`client-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                      <Box
                        className={`status-chip ${company.isActive ? 'active' : 'inactive'} ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                        sx={{
                          backgroundColor: company.isActive ? '#14BA6D' : '#F2F4F7',
                          color: company.isActive ? '#FFFFFF' : '#364254',
                          padding: '4px 8px',
                          borderRadius: '12px',
                          fontSize: '0.75rem',
                          fontWeight: 500,
                          display: 'inline-flex',
                          alignItems: 'center',
                          gap: 0.5
                        }}
                      >
                        <StatusIcon
                          sx={{
                            color: company.isActive ? '#FFFFFF' : '#6C778B',
                            fontSize: '0.75rem'
                          }}
                        />
                        {company.isActive ? 'Active' : 'Inactive'}
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      {company.users?.some(user => user.role === 'admin' && user.isActive) ? (
                        <Box
                          className={`dashboard-access-button ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                          sx={{
                            backgroundColor: 'transparent',
                            border: '1px solid #1B4672',
                            borderRadius: '16px',
                            padding: '6px 12px',
                            fontSize: '0.75rem',
                            fontWeight: 500,
                            color: '#1B4672',
                            display: 'inline-block',
                            cursor: 'pointer',
                            transition: 'all 0.2s ease-in-out',
                            '&:hover': {
                              backgroundColor: 'rgba(27, 70, 114, 0.1)',
                              transform: 'scale(1.02)'
                            }
                          }}
                          onClick={(e) => {
                            e.stopPropagation();
                            const adminUser = company.users?.find(user => user.role === 'admin' && user.isActive);
                            if (adminUser) {
                              handleAccessDashboard(adminUser);
                            }
                          }}
                        >
                          Dashboard Access
                        </Box>
                      ) : (
                        <Typography variant="body2" sx={{ color: '#1B4672' }}>
                          No admin users
                        </Typography>
                      )}
                    </TableCell>
                    <TableCell align="center">
                      <Box 
                        className={`action-button-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                        sx={{ 
                          display: 'flex', 
                          alignItems: 'center',
                          backgroundColor: '#FAFBFD',
                          border: '1px solid #D5D5D5',
                          borderRadius: '16px',
                          padding: '2px 4px',
                          width: 'fit-content',
                          margin: '0 auto',
                          height: '32px'
                        }}>
                        <Tooltip title="Edit company settings">
                          <IconButton
                            size="small"
                            className={`action-icon-button edit-icon ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleEditCompany(company);
                            }}
                            sx={{ 
                              width: 28,
                              height: 28,
                              color: theme.palette.mode === 'dark' ? 'white' : 'black',
                              position: 'relative',
                              mx: '6px',
                              '&:hover': {
                                backgroundColor: '#F0F2F5',
                                borderRadius: '12px',
                                transition: 'all 0.2s ease-in-out'
                              }
                            }}
                          >
                            <svg 
                              className={`edit-icon-svg ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                              width="16" 
                              height="16" 
                              viewBox="0 0 18 18" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <g opacity="0.6">
                                <path fillRule="evenodd" clipRule="evenodd" d="M9.69659 10.4247L7.22192 10.7787L7.57526 8.30334L13.9393 1.93934C14.525 1.35355 15.4748 1.35355 16.0606 1.93934C16.6464 2.52513 16.6464 3.47489 16.0606 4.06068L9.69659 10.4247Z" stroke="black" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M13.2319 2.64648L15.3533 4.76782" stroke="black" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                <path d="M13.5 10.5V15.5C13.5 16.0523 13.0523 16.5 12.5 16.5H2.5C1.94772 16.5 1.5 16.0523 1.5 15.5V5.5C1.5 4.94772 1.94772 4.5 2.5 4.5H7.5" stroke="black" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                              </g>
                            </svg>
                          </IconButton>
                        </Tooltip>
                        
                        <Tooltip title={
                          !company.isActive 
                            ? 'Cannot add users - Company is disabled' 
                            : (company.users?.length || 0) >= company.maxUsers
                              ? 'Maximum users reached'
                              : 'Add new user to company'
                        }>
                          <Box component="span" sx={{ display: 'inline-block' }}>
                            <IconButton
                              size="small"
                              className={`action-icon-button add-user-icon ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                              onClick={(e) => {
                                e.stopPropagation();
                                setSelectedCompany(company);
                                setCreateUserDialogOpen(true);
                              }}
                              disabled={!company.isActive || (company.users?.length || 0) >= company.maxUsers}
                              sx={{ 
                                width: 28,
                                height: 28,
                                color: theme.palette.mode === 'dark' ? 'white' : 'black',
                                position: 'relative',
                                mx: '6px',
                                opacity: (!company.isActive || (company.users?.length || 0) >= company.maxUsers) ? 0.5 : 1,
                                '&:hover': {
                                  backgroundColor: '#F0F2F5',
                                  borderRadius: '12px',
                                  transition: 'all 0.2s ease-in-out'
                                }
                              }}
                            >
                              <svg 
                                className={`add-user-icon-svg ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                width="16" 
                                height="16" 
                                viewBox="0 0 21 14" 
                                fill="none" 
                                xmlns="http://www.w3.org/2000/svg"
                              >
                                <path d="M15.9545 8.75V6.125H13.2273V4.375H15.9545V1.75H17.7727V4.375H20.5V6.125H17.7727V8.75H15.9545ZM7.77273 7C6.77273 7 5.91667 6.65729 5.20455 5.97187C4.49242 5.28646 4.13636 4.4625 4.13636 3.5C4.13636 2.5375 4.49242 1.71354 5.20455 1.02813C5.91667 0.342708 6.77273 0 7.77273 0C8.77273 0 9.62879 0.342708 10.3409 1.02813C11.053 1.71354 11.4091 2.5375 11.4091 3.5C11.4091 4.4625 11.053 5.28646 10.3409 5.97187C9.62879 6.65729 8.77273 7 7.77273 7ZM0.5 14V11.55C0.5 11.0542 0.632727 10.5986 0.898182 10.1832C1.16364 9.76792 1.51576 9.45058 1.95455 9.23125C2.89394 8.77917 3.84848 8.44025 4.81818 8.2145C5.78788 7.98875 6.77273 7.87558 7.77273 7.875C8.77273 7.87442 9.75758 7.98758 10.7273 8.2145C11.697 8.44142 12.6515 8.78033 13.5909 9.23125C14.0303 9.45 14.3827 9.76733 14.6482 10.1832C14.9136 10.5992 15.0461 11.0548 15.0455 11.55V14H0.5Z" fill="#646465"/>
                              </svg>
                            </IconButton>
                          </Box>
                        </Tooltip>
                        
                        <Tooltip title="Delete company">
                          <IconButton
                            size="small"
                            className={`action-icon-button delete-icon ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                            onClick={(e) => {
                              e.stopPropagation();
                              setSelectedCompany(company);
                              setDeleteTarget('company');
                              setDeleteDialogOpen(true);
                            }}
                            sx={{ 
                              width: 28,
                              height: 28,
                              color: theme.palette.mode === 'dark' ? 'white' : 'black',
                              position: 'relative',
                              mx: '6px',
                              '&:hover': {
                                backgroundColor: '#F0F2F5',
                                borderRadius: '12px',
                                transition: 'all 0.2s ease-in-out'
                              }
                            }}
                          >
                            <svg 
                              className={`delete-icon-svg ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                              width="16" 
                              height="16" 
                              viewBox="0 0 18 16" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <path fillRule="evenodd" clipRule="evenodd" d="M13.5001 15.2012H5.10015C4.4374 15.2012 3.90015 14.6639 3.90015 14.0012V3.20117H14.7001V14.0012C14.7001 14.6639 14.1629 15.2012 13.5001 15.2012Z" stroke="#EF3826" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M7.5001 11.6008V6.80078" stroke="#EF3826" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M11.1 11.6008V6.80078" stroke="#EF3826" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path d="M1.5 3.20117H17.1" stroke="#EF3826" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                              <path fillRule="evenodd" clipRule="evenodd" d="M11.1 0.800781H7.50005C6.83731 0.800781 6.30005 1.33804 6.30005 2.00078V3.20078H12.3V2.00078C12.3 1.33804 11.7628 0.800781 11.1 0.800781Z" stroke="#EF3826" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                            </svg>
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={`${company.isActive ? 'Disable company (auto-disables all users)' : 'Enable company (auto-enables all users)'}`}>
                        <Box 
                          onClick={(e) => {
                            e.stopPropagation();
                            setToggleCompany(company);
                            setToggleConfirmDialogOpen(true);
                          }}
                          sx={{
                            width: 44,
                            height: 24,
                            borderRadius: 12,
                            backgroundColor: company.isActive ? theme.palette.success.main : theme.palette.grey[200],
                            cursor: 'pointer',
                            position: 'relative',
                            transition: 'background-color 200ms ease-in-out',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '2px',
                            boxSizing: 'border-box'
                          }}
                        >
                          <Box
                            sx={{
                              width: 20,
                              height: 20,
                              borderRadius: '50%',
                              backgroundColor: theme.palette.mode === 'dark' ? '#081028' : '#1B4672',
                              boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                              transition: 'transform 200ms ease-in-out',
                              transform: company.isActive ? 'translateX(20px)' : 'translateX(0px)',
                              flexShrink: 0
                            }}
                          />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      <Tooltip title={expandedRow === company._id ? 'Collapse row' : 'Expand row'}>
                        <IconButton
                          size="small"
                          className={`expand-collapse-button ${expandedRow === company._id ? 'expanded' : 'collapsed'} ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                          onClick={(e) => {
                            e.stopPropagation();
                            handleExpandRow(company._id);
                          }}
                          sx={{
                            backgroundColor: expandedRow === company._id ? '#FEE2E2' : '#CCF4CD',
                            color: expandedRow === company._id ? '#DC2626' : '#00C805',
                            width: 32,
                            height: 32,
                            borderRadius: '50%',
                            transition: 'all 0.3s ease-in-out',
                            '&:hover': {
                              backgroundColor: expandedRow === company._id ? '#FECACA' : '#B8E6B8',
                              transform: 'scale(1.05)',
                            }
                          }}
                        >
                          {expandedRow === company._id ? (
                            <RemoveIcon sx={{ fontSize: 16, transition: 'transform 0.3s ease-in-out' }} />
                          ) : (
                            <AddIcon sx={{ fontSize: 16, transition: 'transform 0.3s ease-in-out' }} />
                          )}
                        </IconButton>
                      </Tooltip>
                    </TableCell>
                  </TableRow>
                  
                  {/* Expanded Row */}
                  <TableRow>
                    <TableCell colSpan={8} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                      <Collapse in={expandedRow === company._id} timeout="auto" unmountOnExit>
                        <Box sx={{ 
                          padding: 3, 
                          backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.02)' : 'rgba(0,0,0,0.02)', 
                          borderRadius: 2,
                          margin: 1,
                          border: `1px solid ${theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(0,0,0,0.1)'}`
                        }}>
                          <Typography 
                            variant="h6" 
                            className={`company-details-title ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                            sx={{ mb: 2, fontWeight: 'bold', color: theme.palette.primary.main }}
                          >
                            Company Details & Users
                          </Typography>
                          
                          {/* Users Table */}
                          {company.users && company.users.length > 0 ? (
                            <Box>
                              <Typography 
                                variant="subtitle1" 
                                className={`user-section-title ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                sx={{ fontWeight: 'medium', mb: 2 }}
                              >
                                Users ({company.users.length})
                              </Typography>
                              <TableContainer 
                                component={Paper} 
                                className={`user-table-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                sx={{ borderRadius: 2, backgroundColor: '#FFFFFF' }}
                              >
                                <Table size="small" className={`user-table ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                  <TableHead>
                                    <TableRow className={`user-table-header ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                      <TableCell className={`user-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>User</TableCell>
                                      <TableCell className={`user-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>Email</TableCell>
                                      <TableCell className={`user-table-header-cell role-column-no-border ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>Role</TableCell>
                                      <TableCell className={`user-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>Status</TableCell>
                                      <TableCell className={`user-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} align="center">Actions</TableCell>
                                      <TableCell className={`user-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} align="center"></TableCell>
                                    </TableRow>
                                  </TableHead>
                                  <TableBody>
                                    {company.users.map((user) => {
                                      const { isOnline } = getUserOnlineStatus(user);
                                      return (
                                        <TableRow key={user._id} className={`user-table-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                          <TableCell className={`user-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                            <Box>
                                              <Typography 
                                                variant="body2" 
                                                className={`user-name ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                sx={{ fontWeight: 'medium' }}
                                              >
                                                {user.clientName}
                                              </Typography>
                                              <Typography 
                                                variant="caption" 
                                                className={`user-username ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                color="text.secondary"
                                              >
                                                @{user.username}
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell className={`user-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                            <Typography 
                                              variant="body2" 
                                              className={`user-email ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                              color="text.secondary"
                                            >
                                              {user.email}
                                            </Typography>
                                          </TableCell>
                                          <TableCell className={`role-column-no-border ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                            <Chip
                                              label={user.role?.toUpperCase() || 'VIEWER'}
                                              size="small"
                                              color={
                                                user.role === 'admin' ? 'error' :
                                                user.role === 'editor' ? 'warning' :
                                                'default'
                                              }
                                              variant="outlined"
                                            />
                                          </TableCell>
                                          <TableCell>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                                              <StatusIcon
                                                sx={{
                                                  color: !company.isActive && !user.isActive
                                                    ? '#f44336'  // Company Disabled - Red
                                                    : !company.isActive && user.isActive
                                                      ? '#ff9800'  // Override Active - Orange
                                                      : company.isActive && !user.isActive
                                                        ? '#f44336'  // User Disabled - Red
                                                        : (isOnline ? '#4caf50' : '#f44336'),  // Online (Green) / Offline (Red)
                                                  fontSize: '0.875rem',
                                                  filter: !company.isActive && !user.isActive
                                                    ? 'drop-shadow(0 0 4px rgba(244, 67, 54, 0.5))'
                                                    : !company.isActive && user.isActive
                                                      ? 'drop-shadow(0 0 4px rgba(255, 152, 0, 0.5))'
                                                      : company.isActive && !user.isActive
                                                        ? 'drop-shadow(0 0 4px rgba(244, 67, 54, 0.5))'
                                                        : (isOnline ? 'drop-shadow(0 0 4px rgba(76, 175, 80, 0.5))' : 'drop-shadow(0 0 4px rgba(244, 67, 54, 0.5))')
                                                }}
                                              />
                                              <Typography 
                                                variant="caption" 
                                                sx={{ 
                                                  color: !company.isActive && !user.isActive
                                                    ? 'error.main'  // Company Disabled - Red
                                                    : !company.isActive && user.isActive
                                                      ? 'warning.main'  // Override Active - Orange
                                                      : company.isActive && !user.isActive
                                                        ? 'error.main'  // User Disabled - Red
                                                        : (isOnline ? 'success.main' : 'error.main'),  // Online (Green) / Offline (Red)
                                                  fontWeight: 500,
                                                  fontSize: '0.75rem'
                                                }}
                                              >
                                                {!company.isActive && !user.isActive
                                                  ? 'Company Disabled'
                                                  : !company.isActive && user.isActive
                                                    ? 'Override Active'
                                                    : company.isActive && !user.isActive
                                                      ? 'User Disabled'
                                                      : (isOnline ? 'Online' : 'Offline')
                                                }
                                              </Typography>
                                            </Box>
                                          </TableCell>
                                          <TableCell align="center">
                                            <Box 
                                              className={`user-action-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                              sx={{ 
                                                display: 'flex', 
                                                alignItems: 'center',
                                                backgroundColor: '#FAFBFD',
                                                border: '1px solid #D5D5D5',
                                                borderRadius: '16px',
                                                padding: '2px',
                                                width: 'fit-content',
                                                margin: '0 auto'
                                              }}>
                                              <Tooltip title="Copy dashboard URL">
                                                <IconButton
                                                  size="small"
                                                  className={`user-action-button copy-url ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                  onClick={() => handleCopyDashboardUrl(user)}
                                                  sx={{ 
                                                    width: 32,
                                                    height: 32,
                                                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                                                    position: 'relative',
                                                    '&:hover': {
                                                      backgroundColor: '#F0F2F5',
                                                      borderRadius: '16px',
                                                      transition: 'all 0.2s ease-in-out'
                                                    }
                                                  }}
                                                >
                                                  <svg 
                                                    className={`copy-icon-svg ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                    width="16" 
                                                    height="16" 
                                                    viewBox="0 0 24 24" 
                                                    fill="none" 
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <path d="M16 1H4c-1.1 0-2 .9-2 2v14h2V3h12zm3 4H8c-1.1 0-2 .9-2 2v14c0 1.1.9 2 2 2h11c1.1 0 2-.9 2-2V7c0-1.1-.9-2-2-2m0 16H8V7h11z" stroke="currentColor" strokeWidth="1.2" fill="none"/>
                                                  </svg>
                                                </IconButton>
                                              </Tooltip>
                                              
                                              
                                              <Tooltip title="Delete user">
                                                <IconButton
                                                  size="small"
                                                  className={`user-action-button delete-user ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                  onClick={() => {
                                                    setSelectedUser(user);
                                                    setDeleteTarget('user');
                                                    setDeleteDialogOpen(true);
                                                  }}
                                                  sx={{ 
                                                    width: 32,
                                                    height: 32,
                                                    color: theme.palette.mode === 'dark' ? 'white' : 'black',
                                                    position: 'relative',
                                                    '&:hover': {
                                                      backgroundColor: '#F0F2F5',
                                                      borderRadius: '16px',
                                                      transition: 'all 0.2s ease-in-out'
                                                    }
                                                  }}
                                                >
                                                  <svg 
                                                    className={`user-delete-icon-svg ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                    width="16" 
                                                    height="16" 
                                                    viewBox="0 0 18 16" 
                                                    fill="none" 
                                                    xmlns="http://www.w3.org/2000/svg"
                                                  >
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M13.5001 15.2012H5.10015C4.4374 15.2012 3.90015 14.6639 3.90015 14.0012V3.20117H14.7001V14.0012C14.7001 14.6639 14.1629 15.2012 13.5001 15.2012Z" stroke="#EF3826" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M7.5001 11.6008V6.80078" stroke="#EF3826" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M11.1 11.6008V6.80078" stroke="#EF3826" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path d="M1.5 3.20117H17.1" stroke="#EF3826" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                                    <path fillRule="evenodd" clipRule="evenodd" d="M11.1 0.800781H7.50005C6.83731 0.800781 6.30005 1.33804 6.30005 2.00078V3.20078H12.3V2.00078C12.3 1.33804 11.7628 0.800781 11.1 0.800781Z" stroke="#EF3826" strokeWidth="1.2" strokeLinecap="round" strokeLinejoin="round"/>
                                                  </svg>
                                                </IconButton>
                                              </Tooltip>
                                            </Box>
                                          </TableCell>
                                          <TableCell align="center">
                                            <Tooltip title={
                                              !company.isActive && !user.isActive 
                                                ? "User and company both disabled"
                                                : !company.isActive && user.isActive
                                                  ? "User enabled (overriding company setting)"
                                                  : company.isActive && !user.isActive
                                                    ? "Enable/disable this user individually"
                                                    : "Enable/disable this user individually"
                                            }>
                                              <Box 
                                                className={`user-toggle-switch ${user.isActive ? 'active' : 'inactive'} ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                onClick={() => handleUserToggle(user._id, user.isActive)}
                                                sx={{
                                                  width: 44,
                                                  height: 24,
                                                  borderRadius: 12,
                                                  backgroundColor: user.isActive ? theme.palette.success.main : theme.palette.grey[200],
                                                  cursor: 'pointer',
                                                  position: 'relative',
                                                  transition: 'background-color 200ms ease-in-out',
                                                  display: 'flex',
                                                  alignItems: 'center',
                                                  padding: '2px',
                                                  boxSizing: 'border-box'
                                                }}
                                              >
                                                <Box
                                                  className={`user-toggle-handle ${user.isActive ? 'active' : 'inactive'} ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                  sx={{
                                                    width: 20,
                                                    height: 20,
                                                    borderRadius: '50%',
                                                    backgroundColor: theme.palette.mode === 'dark' ? '#081028' : '#1B4672',
                                                    boxShadow: '0 2px 4px rgba(0,0,0,0.2)',
                                                    transition: 'transform 200ms ease-in-out',
                                                    transform: user.isActive ? 'translateX(20px)' : 'translateX(0px)',
                                                    flexShrink: 0
                                                  }}
                                                />
                                              </Box>
                                            </Tooltip>
                                          </TableCell>
                                        </TableRow>
                                      );
                                    })}
                                  </TableBody>
                                </Table>
                              </TableContainer>
                            </Box>
                          ) : (
                            <Alert severity="info" sx={{ borderRadius: 2 }}>
                              No users found for this company. Click the "Add User" button to create the first user.
                            </Alert>
                          )}
                        </Box>
                      </Collapse>
                    </TableCell>
                  </TableRow>
                  </React.Fragment>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          )}

          {/* Mobile Table */}
          {isMobile && (
            <TableContainer 
              component={Paper} 
              className={`mobile-table-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
            >
              <Table className={`mobile-table ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                <TableHead>
                  <TableRow className={`mobile-table-header ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                    <TableCell className={`mobile-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>Company</TableCell>
                    <TableCell className={`mobile-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>Status</TableCell>
                    <TableCell className={`mobile-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}></TableCell>
                    <TableCell className={`mobile-table-header-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}></TableCell>
                  </TableRow>
                </TableHead>
                <TableBody>
                  {paginatedCompanies.map((company) => (
                    <React.Fragment key={company._id}>
                      {/* Mobile Company Row */}
                      <TableRow className={`mobile-table-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                        {/* Column 1: Company Name */}
                        <TableCell className={`mobile-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                          <Typography 
                            variant="body2" 
                            className={`table-company-name ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                            sx={{ fontWeight: 'medium' }}
                          >
                            {company.companyName}
                          </Typography>
                        </TableCell>
                        
                        {/* Column 2: Status */}
                        <TableCell className={`mobile-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                          <Box
                            className={`status-chip ${company.isActive ? 'active' : 'inactive'} ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                            sx={{
                              backgroundColor: company.isActive ? '#14BA6D' : '#F2F4F7',
                              color: company.isActive ? '#FFFFFF' : '#364254',
                              padding: '4px 8px',
                              borderRadius: '12px',
                              fontSize: '0.75rem',
                              fontWeight: 500,
                              display: 'inline-flex',
                              alignItems: 'center',
                              gap: 0.5
                            }}
                          >
                            <StatusIcon
                              sx={{
                                color: company.isActive ? '#FFFFFF' : '#6C778B',
                                fontSize: '0.75rem'
                              }}
                            />
                            {company.isActive ? 'Active' : 'Inactive'}
                          </Box>
                        </TableCell>
                        
                        {/* Column 3: Expand/Collapse */}
                        <TableCell className={`mobile-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} align="center">
                          <IconButton
                            size="small"
                            className={`expand-collapse-button ${expandedRow === company._id ? 'expanded' : 'collapsed'} ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                            onClick={() => handleExpandRow(company._id)}
                            sx={{
                              backgroundColor: expandedRow === company._id ? '#FEE2E2' : '#CCF4CD',
                              color: expandedRow === company._id ? '#DC2626' : '#00C805',
                              width: 28,
                              height: 28,
                              borderRadius: '50%',
                            }}
                          >
                            {expandedRow === company._id ? (
                              <RemoveIcon sx={{ fontSize: 14 }} />
                            ) : (
                              <AddIcon sx={{ fontSize: 14 }} />
                            )}
                          </IconButton>
                        </TableCell>
                        
                        {/* Column 4: Three Dots Menu */}
                        <TableCell className={`mobile-table-cell ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`} align="center">
                          <Box
                            onClick={(e) => handleMobileMenuOpen(e, company)}
                            sx={{ cursor: 'pointer', padding: '8px' }}
                          >
                            <svg 
                              className={`three-dots-icon ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                              width="16" 
                              height="16" 
                              viewBox="0 0 3 12" 
                              fill="none" 
                              xmlns="http://www.w3.org/2000/svg"
                            >
                              <circle cx="1.5" cy="1.5" r="1.5" fill="#667085"/>
                              <circle cx="1.5" cy="6" r="1.5" fill="#667085"/>
                              <circle cx="1.5" cy="10.5" r="1.5" fill="#667085"/>
                            </svg>
                          </Box>
                        </TableCell>
                      </TableRow>
                      
                      {/* Mobile Expanded Row */}
                      {expandedRow === company._id && (
                        <TableRow>
                          <TableCell colSpan={4} sx={{ paddingBottom: 0, paddingTop: 0 }}>
                            <Collapse in={expandedRow === company._id} timeout="auto" unmountOnExit>
                              <Box className={`mobile-user-expanded ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                {company.users && company.users.length > 0 ? (
                                  <>
                                    {company.users.map((user) => {
                                      const { isOnline } = getUserOnlineStatus(user);
                                      return (
                                        <Box key={user._id} className={`mobile-user-row ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                          {/* User Info */}
                                          <Box className={`mobile-user-info ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                            <Typography 
                                              variant="body2" 
                                              className={`mobile-user-name ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                            >
                                              {user.clientName}
                                            </Typography>
                                            <Typography 
                                              variant="caption" 
                                              className={`mobile-user-email ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                            >
                                              {user.email}
                                            </Typography>
                                          </Box>
                                          
                                          {/* Status */}
                                          <Box className={`mobile-user-status ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                              <StatusIcon
                                                sx={{
                                                  color: isOnline ? '#4caf50' : '#f44336',
                                                  fontSize: '0.75rem'
                                                }}
                                              />
                                              <Typography variant="caption" sx={{ fontSize: '0.75rem' }}>
                                                {isOnline ? 'Online' : 'Offline'}
                                              </Typography>
                                            </Box>
                                          </Box>
                                          
                                          {/* Toggle */}
                                          <Box className={`mobile-user-toggle ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}>
                                            <Box 
                                              className={`user-toggle-switch ${user.isActive ? 'active' : 'inactive'} ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                              onClick={() => handleUserToggle(user._id, user.isActive)}
                                              sx={{
                                                width: 36,
                                                height: 20,
                                                borderRadius: 10,
                                                backgroundColor: user.isActive ? theme.palette.success.main : theme.palette.grey[200],
                                                cursor: 'pointer',
                                                position: 'relative',
                                                transition: 'background-color 200ms ease-in-out',
                                                display: 'flex',
                                                alignItems: 'center',
                                                padding: '2px',
                                              }}
                                            >
                                              <Box
                                                className={`user-toggle-handle ${user.isActive ? 'active' : 'inactive'} ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                                sx={{
                                                  width: 16,
                                                  height: 16,
                                                  borderRadius: '50%',
                                                  backgroundColor: theme.palette.mode === 'dark' ? '#081028' : '#1B4672',
                                                  transform: user.isActive ? 'translateX(16px)' : 'translateX(0px)',
                                                  transition: 'transform 200ms ease-in-out',
                                                }}
                                              />
                                            </Box>
                                          </Box>
                                        </Box>
                                      );
                                    })}
                                    
                                    {/* Access Dashboard Button - Mobile Only */}
                                    {company.users?.some(user => user.role === 'admin' && user.isActive) && (
                                      <Button
                                        className={`mobile-access-dashboard ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                                        onClick={() => {
                                          const adminUser = company.users?.find(user => user.role === 'admin' && user.isActive);
                                          if (adminUser) {
                                            handleAccessDashboard(adminUser);
                                          }
                                        }}
                                      >
                                        Access Dashboard
                                      </Button>
                                    )}
                                  </>
                                ) : (
                                  <Typography variant="body2" sx={{ textAlign: 'center', py: 2 }}>
                                    No users found for this company.
                                  </Typography>
                                )}
                              </Box>
                            </Collapse>
                          </TableCell>
                        </TableRow>
                      )}
                    </React.Fragment>
                  ))}
                </TableBody>
              </Table>
            </TableContainer>
          )}
        
          {/* Pagination Controls */}
          <Box 
            className={`pagination-container ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
            sx={{ 
              display: 'flex', 
              justifyContent: 'space-between', 
              alignItems: 'center',
              mt: 2,
              px: 2
            }}>
            {/* Left side - Showing text */}
            <Typography 
              variant="body2" 
              className={`pagination-info ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ 
                color: '#667085', 
                fontFamily: 'Inter, sans-serif',
                fontSize: '0.875rem'
              }}>
              Showing {startIndex + 1}-{endIndex.toString().padStart(2, '0')} of {totalItems}
            </Typography>
            
            {/* Right side - Navigation arrows */}
            {totalPages > 1 && (
              <Box 
                className={`pagination-controls ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                sx={{ 
                  display: 'flex', 
                  alignItems: 'center',
                  backgroundColor: '#FAFBFD',
                  border: '1px solid #D5D5D5',
                  borderRadius: '16px',
                  padding: '2px 4px',
                  height: '32px'
                }}>
                <IconButton
                  size="small"
                  className={`pagination-button prev ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                  onClick={handlePreviousPage}
                  disabled={currentPage === 1}
                  sx={{ 
                    width: 28,
                    height: 28,
                    mx: '2px',
                    color: currentPage === 1 ? '#A0A0A0' : '#667085',
                    '&:hover': {
                      backgroundColor: currentPage === 1 ? 'transparent' : '#F0F2F5',
                      borderRadius: '12px',
                    }
                  }}
                >
                  <ChevronLeftIcon sx={{ fontSize: '18px' }} />
                </IconButton>
                
                <IconButton
                  size="small"
                  className={`pagination-button next ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                  onClick={handleNextPage}
                  disabled={currentPage === totalPages}
                  sx={{ 
                    width: 28,
                    height: 28,
                    mx: '2px',
                    color: currentPage === totalPages ? '#A0A0A0' : '#667085',
                    '&:hover': {
                      backgroundColor: currentPage === totalPages ? 'transparent' : '#F0F2F5',
                      borderRadius: '12px',
                    }
                  }}
                >
                  <ChevronRightIcon sx={{ fontSize: '18px' }} />
                </IconButton>
              </Box>
            )}
          </Box>
        </>
      )}

      {/* Create Company Dialog */}
      <Dialog 
        open={createCompanyDialogOpen} 
        onClose={() => setCreateCompanyDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0,0,0,0.3)' 
              : '0 8px 32px rgba(0,0,0,0.12)',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }
        }}
      >
        <form onSubmit={companyFormik.handleSubmit}>
          {/* Header */}
          <Box sx={{ p: 3, pb: 2, textAlign: 'center' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1
              }}
            >
              Create Company
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.9rem'
              }}
            >
              Fill out the form below to add a new company to the system.
            </Typography>
          </Box>

          {/* Form Content */}
          <DialogContent sx={{ px: 3, py: 1 }}>
            <Stack spacing={2.5}>
              {/* Company Name */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Company Name
                </Typography>
                <TextField 
                  fullWidth 
                  id="companyName" 
                  name="companyName" 
                  placeholder="The Impression"
                  value={companyFormik.values.companyName} 
                  onChange={companyFormik.handleChange} 
                  onBlur={companyFormik.handleBlur}
                  error={companyFormik.touched.companyName && Boolean(companyFormik.errors.companyName)} 
                  helperText={companyFormik.touched.companyName && companyFormik.errors.companyName}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                    },
                  }}
                />
              </Box>

              {/* Company Industry */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Company Industry
                </Typography>
                <TextField 
                  fullWidth 
                  id="companyIndustry" 
                  name="companyIndustry" 
                  placeholder="Digital Marketing"
                  value={companyFormik.values.companyIndustry} 
                  onChange={companyFormik.handleChange} 
                  onBlur={companyFormik.handleBlur}
                  error={companyFormik.touched.companyIndustry && Boolean(companyFormik.errors.companyIndustry)} 
                  helperText={companyFormik.touched.companyIndustry && companyFormik.errors.companyIndustry}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                    },
                  }}
                />
              </Box>

              {/* Max Users */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Maximum Users
                </Typography>
                <TextField 
                  fullWidth 
                  id="maxUsers" 
                  name="maxUsers" 
                  type="number"
                  placeholder="10"
                  value={companyFormik.values.maxUsers} 
                  onChange={companyFormik.handleChange} 
                  onBlur={companyFormik.handleBlur}
                  error={companyFormik.touched.maxUsers && Boolean(companyFormik.errors.maxUsers)} 
                  helperText={companyFormik.touched.maxUsers && companyFormik.errors.maxUsers}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                    },
                  }}
                />
              </Box>
            </Stack>
          </DialogContent>

          {/* Actions */}
          <Box sx={{ p: 3, pt: 2 }}>
            <Button 
              type="submit" 
              variant="contained"
              fullWidth
              disabled={companyFormik.isSubmitting}
              className={`popup-button-primary ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ 
                '&:disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                }
              }}
            >
              {companyFormik.isSubmitting ? 'Creating...' : 'Create Company'}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button 
                onClick={() => setCreateCompanyDialogOpen(false)}
                className={`popup-button-secondary ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                sx={{ 
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Dialog>

      {/* Create User Dialog */}
      <Dialog 
        open={createUserDialogOpen} 
        onClose={() => setCreateUserDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0,0,0,0.3)' 
              : '0 8px 32px rgba(0,0,0,0.12)',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }
        }}
      >
        <form onSubmit={userFormik.handleSubmit}>
          {/* Header */}
          <Box sx={{ p: 3, pb: 2, textAlign: 'center' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1
              }}
            >
              Add User to {selectedCompany?.companyName}
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.9rem'
              }}
            >
              Fill out the form below to add a new user to this company.
            </Typography>
          </Box>

          {/* Form Content */}
          <DialogContent sx={{ px: 3, py: 1 }}>
            <Stack spacing={2.5}>
              {/* Full Name */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Full Name
                </Typography>
                <TextField 
                  fullWidth 
                  id="clientName" 
                  name="clientName" 
                  placeholder="John Doe"
                  value={userFormik.values.clientName} 
                  onChange={userFormik.handleChange} 
                  onBlur={userFormik.handleBlur}
                  error={userFormik.touched.clientName && Boolean(userFormik.errors.clientName)} 
                  helperText={userFormik.touched.clientName && userFormik.errors.clientName}
                  variant="outlined"
                  className={`client-form-input-name ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                />
              </Box>

              {/* Email */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Email Address
                </Typography>
                <TextField 
                  fullWidth 
                  id="email" 
                  name="email" 
                  type="email"
                  placeholder="john@example.com"
                  value={userFormik.values.email} 
                  onChange={userFormik.handleChange} 
                  onBlur={userFormik.handleBlur}
                  error={userFormik.touched.email && Boolean(userFormik.errors.email)} 
                  helperText={userFormik.touched.email && userFormik.errors.email}
                  variant="outlined"
                  className={`client-form-input-email ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                />
              </Box>

              {/* Username */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Username
                </Typography>
                <TextField 
                  fullWidth 
                  id="username" 
                  name="username" 
                  placeholder="johndoe"
                  value={userFormik.values.username} 
                  onChange={userFormik.handleChange} 
                  onBlur={userFormik.handleBlur}
                  error={userFormik.touched.username && Boolean(userFormik.errors.username)} 
                  helperText={userFormik.touched.username && userFormik.errors.username}
                  variant="outlined"
                  className={`client-form-input-username ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                />
              </Box>

              {/* Password */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Password
                </Typography>
                <TextField 
                  fullWidth 
                  id="password" 
                  name="password" 
                  type={showUserPassword ? 'text' : 'password'}
                  placeholder="Enter password"
                  value={userFormik.values.password} 
                  onChange={userFormik.handleChange} 
                  onBlur={userFormik.handleBlur}
                  error={userFormik.touched.password && Boolean(userFormik.errors.password)} 
                  helperText={userFormik.touched.password && userFormik.errors.password}
                  variant="outlined"
                  InputProps={{
                    endAdornment: (
                      <InputAdornment position="end">
                        <IconButton
                          aria-label="toggle password visibility"
                          onClick={() => setShowUserPassword(!showUserPassword)}
                          edge="end"
                          sx={{ 
                            color: theme.palette.text.secondary,
                            mr: 1
                          }}
                        >
                          {showUserPassword ? <VisibilityOffIcon /> : <VisibilityIcon />}
                        </IconButton>
                      </InputAdornment>
                    ),
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                  }}
                  className={`client-form-input-password ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                />
              </Box>

              {/* Role */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Role
                </Typography>
                <FormControl fullWidth>
                  <Select
                    id="role"
                    name="role"
                    value={userFormik.values.role}
                    onChange={userFormik.handleChange}
                    error={userFormik.touched.role && Boolean(userFormik.errors.role)}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: '30px',
                        backgroundColor: theme.palette.mode === 'dark' 
                          ? 'rgba(255, 255, 255, 0.05)' 
                          : 'rgba(0, 0, 0, 0.02)',
                        '& fieldset': {
                          borderColor: theme.palette.divider,
                        },
                        '&:hover fieldset': {
                          borderColor: theme.palette.primary.main,
                        },
                        '&.Mui-focused fieldset': {
                          borderColor: theme.palette.primary.main,
                          borderWidth: 2,
                        },
                      },
                      '& .MuiSelect-select': {
                        py: 1.5,
                        px: 2,
                      },
                    }}
                  >
                    <MenuItem value="admin">Admin</MenuItem>
                    <MenuItem value="editor">Editor</MenuItem>
                    <MenuItem value="viewer">Viewer</MenuItem>
                  </Select>
                  {userFormik.touched.role && userFormik.errors.role && (
                    <FormHelperText error sx={{ mt: 1, ml: 2 }}>{userFormik.errors.role}</FormHelperText>
                  )}
                </FormControl>
              </Box>
            </Stack>
          </DialogContent>

          {/* Actions */}
          <Box sx={{ p: 3, pt: 2 }}>
            <Button 
              type="submit" 
              variant="contained"
              fullWidth
              disabled={userFormik.isSubmitting}
              className={`popup-button-primary ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ 
                '&:disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                }
              }}
            >
              {userFormik.isSubmitting ? 'Adding...' : 'Add User'}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button 
                onClick={() => setCreateUserDialogOpen(false)}
                className={`popup-button-secondary ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                sx={{ 
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Dialog>

      {/* Edit Company Dialog */}
      <Dialog 
        open={editCompanyDialogOpen} 
        onClose={() => setEditCompanyDialogOpen(false)} 
        maxWidth="sm" 
        fullWidth
        PaperProps={{
          sx: {
            borderRadius: '16px',
            boxShadow: theme.palette.mode === 'dark' 
              ? '0 8px 32px rgba(0,0,0,0.3)' 
              : '0 8px 32px rgba(0,0,0,0.12)',
            border: `1px solid ${theme.palette.divider}`,
            backgroundColor: theme.palette.background.paper,
          }
        }}
      >
        <form onSubmit={editCompanyFormik.handleSubmit}>
          {/* Header */}
          <Box sx={{ p: 3, pb: 2, textAlign: 'center' }}>
            <Typography 
              variant="h5" 
              sx={{ 
                fontWeight: 700,
                color: theme.palette.text.primary,
                mb: 1
              }}
            >
              Edit Company Settings
            </Typography>
            <Typography 
              variant="body2" 
              sx={{ 
                color: theme.palette.text.secondary,
                fontSize: '0.9rem'
              }}
            >
              Update the company information below.
            </Typography>
          </Box>

          {/* Form Content */}
          <DialogContent sx={{ px: 3, py: 1 }}>
            <Alert severity="info" sx={{ mb: 3, borderRadius: 2 }}>
              <Typography variant="body2">
                <strong>Note:</strong> Changing the maximum users limit will affect the company's ability to add new users.
                {selectedCompany && (selectedCompany.users?.length || 0) > editCompanyFormik.values.maxUsers && (
                  <span style={{ color: 'orange' }}>
                    <br />⚠️ Warning: This company currently has {selectedCompany.users?.length || 0} users, 
                    which exceeds the new limit of {editCompanyFormik.values.maxUsers}. 
                    Existing users will remain active, but no new users can be added until the limit is increased.
                  </span>
                )}
              </Typography>
            </Alert>
            
            <Stack spacing={2.5}>
              {/* Company Name */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Company Name
                </Typography>
                <TextField 
                  fullWidth 
                  id="companyName" 
                  name="companyName" 
                  placeholder="The Impression"
                  value={editCompanyFormik.values.companyName} 
                  onChange={editCompanyFormik.handleChange} 
                  onBlur={editCompanyFormik.handleBlur}
                  error={editCompanyFormik.touched.companyName && Boolean(editCompanyFormik.errors.companyName)} 
                  helperText={editCompanyFormik.touched.companyName && editCompanyFormik.errors.companyName}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                    },
                  }}
                />
              </Box>

              {/* Company Industry */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Company Industry
                </Typography>
                <TextField 
                  fullWidth 
                  id="companyIndustry" 
                  name="companyIndustry" 
                  placeholder="Digital Marketing"
                  value={editCompanyFormik.values.companyIndustry} 
                  onChange={editCompanyFormik.handleChange} 
                  onBlur={editCompanyFormik.handleBlur}
                  error={editCompanyFormik.touched.companyIndustry && Boolean(editCompanyFormik.errors.companyIndustry)} 
                  helperText={editCompanyFormik.touched.companyIndustry && editCompanyFormik.errors.companyIndustry}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                    },
                  }}
                />
              </Box>

              {/* Max Users */}
              <Box>
                <Typography 
                  variant="body2" 
                  sx={{ 
                    fontWeight: 600,
                    color: theme.palette.text.primary,
                    mb: 1
                  }}
                >
                  Maximum Users
                </Typography>
                <TextField 
                  fullWidth 
                  id="maxUsers" 
                  name="maxUsers" 
                  type="number"
                  placeholder="10"
                  value={editCompanyFormik.values.maxUsers} 
                  onChange={editCompanyFormik.handleChange} 
                  onBlur={editCompanyFormik.handleBlur}
                  error={editCompanyFormik.touched.maxUsers && Boolean(editCompanyFormik.errors.maxUsers)} 
                  helperText={editCompanyFormik.touched.maxUsers && editCompanyFormik.errors.maxUsers}
                  InputProps={{
                    inputProps: { min: 1, max: 1000 }
                  }}
                  variant="outlined"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: '30px',
                      backgroundColor: theme.palette.mode === 'dark' 
                        ? 'rgba(255, 255, 255, 0.05)' 
                        : 'rgba(0, 0, 0, 0.02)',
                      '& fieldset': {
                        borderColor: theme.palette.divider,
                      },
                      '&:hover fieldset': {
                        borderColor: theme.palette.primary.main,
                      },
                      '&.Mui-focused fieldset': {
                        borderColor: theme.palette.primary.main,
                        borderWidth: 2,
                      },
                    },
                    '& .MuiInputBase-input': {
                      py: 1.5,
                      px: 2,
                    },
                  }}
                />
              </Box>
            </Stack>
          </DialogContent>

          {/* Actions */}
          <Box sx={{ p: 3, pt: 2 }}>
            <Button 
              type="submit" 
              variant="contained"
              fullWidth
              disabled={editCompanyFormik.isSubmitting}
              className={`popup-button-primary ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
              sx={{ 
                '&:disabled': {
                  backgroundColor: theme.palette.action.disabledBackground,
                  color: theme.palette.action.disabled,
                }
              }}
            >
              {editCompanyFormik.isSubmitting ? 'Updating...' : 'Update Company'}
            </Button>
            
            <Box sx={{ textAlign: 'center', mt: 2 }}>
              <Button 
                onClick={() => setEditCompanyDialogOpen(false)}
                className={`popup-button-secondary ${theme.palette.mode === 'dark' ? 'dark-mode' : 'light-mode'}`}
                sx={{ 
                  textTransform: 'none'
                }}
              >
                Cancel
              </Button>
            </Box>
          </Box>
        </form>
      </Dialog>

      {/* Delete Confirmation Dialog */}
      <Dialog 
        open={deleteDialogOpen} 
        onClose={() => setDeleteDialogOpen(false)}
        maxWidth="xs"
        fullWidth={false}
        sx={{
          zIndex: 9999,
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            backgroundColor: theme.palette.custom.background.modal,
            border: theme.palette.mode === 'dark' ? '1px solid #ffffff' : '1px solid #e2e8f0',
            position: 'relative',
            zIndex: 10000,
            width: '400px',
            maxWidth: '400px',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
          }
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          {/* Header */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.mode === 'dark' ? '#ffffff' : '#202224',
              mb: 2,
              fontSize: '1.5rem',
              fontFamily: '"Nunito Sans", sans-serif',
              letterSpacing: '-0.11px',
            }}
          >
            🗑️ Delete {deleteTarget === 'company' ? 'Company' : 'User'}
          </Typography>
          
          {/* Body */}
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.mode === 'dark' ? '#ffffff' : '#666',
              mb: 4,
              fontSize: '1rem',
              lineHeight: 1.5,
              fontFamily: '"Nunito Sans", sans-serif',
              letterSpacing: '-0.06px',
              opacity: 0.8,
            }}
          >
            Are you sure you want to delete <strong>{deleteTarget === 'company' ? selectedCompany?.companyName : selectedUser?.clientName}</strong>? This action cannot be undone.
          </Typography>
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setDeleteDialogOpen(false)}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                borderColor: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                color: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                fontFamily: '"Nunito Sans", sans-serif',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(27,70,114,0.1)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={handleDelete}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: '#f44336',
                color: '#ffffff',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                fontFamily: '"Nunito Sans", sans-serif',
                '&:hover': {
                  backgroundColor: '#d32f2f',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              Delete
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Toggle Confirmation Dialog */}
      <Dialog 
        open={toggleConfirmDialogOpen} 
        onClose={() => setToggleConfirmDialogOpen(false)}
        maxWidth="xs"
        fullWidth={false}
        sx={{
          zIndex: 9999,
        }}
        PaperProps={{
          sx: {
            borderRadius: 3,
            boxShadow: '0 20px 40px rgba(0, 0, 0, 0.15)',
            backgroundColor: theme.palette.custom.background.modal,
            border: theme.palette.mode === 'dark' ? '1px solid #ffffff' : '1px solid #e2e8f0',
            position: 'relative',
            zIndex: 10000,
            width: '400px',
            maxWidth: '400px',
          }
        }}
        BackdropProps={{
          sx: {
            backgroundColor: 'rgba(0, 0, 0, 0.5)',
            zIndex: 9998,
          }
        }}
      >
        <DialogContent sx={{ p: 4, textAlign: 'center' }}>
          {/* Header */}
          <Typography
            variant="h5"
            sx={{
              fontWeight: 700,
              color: theme.palette.mode === 'dark' ? '#ffffff' : '#202224',
              mb: 2,
              fontSize: '1.5rem',
              fontFamily: '"Nunito Sans", sans-serif',
              letterSpacing: '-0.11px',
            }}
          >
            {toggleCompany?.isActive ? '⚠️ Disable Company' : '✅ Enable Company'}
          </Typography>
          
          {/* Body */}
          <Typography
            variant="body1"
            sx={{
              color: theme.palette.mode === 'dark' ? '#ffffff' : '#666',
              mb: 4,
              fontSize: '1rem',
              lineHeight: 1.5,
              fontFamily: '"Nunito Sans", sans-serif',
              letterSpacing: '-0.06px',
              opacity: 0.8,
            }}
          >
            Are you sure you want to {toggleCompany?.isActive ? 'disable' : 'enable'} <strong>{toggleCompany?.companyName}</strong>? {toggleCompany?.isActive ? 'This will automatically disable all users in this company.' : 'This will automatically enable all users in this company.'}
          </Typography>
          
          {/* Action Buttons */}
          <Box sx={{ display: 'flex', gap: 2 }}>
            <Button
              variant="outlined"
              onClick={() => setToggleConfirmDialogOpen(false)}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                borderColor: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                color: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                fontFamily: '"Nunito Sans", sans-serif',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? 'rgba(255,255,255,0.1)' : 'rgba(27,70,114,0.1)',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              Cancel
            </Button>
            <Button
              variant="contained"
              onClick={() => {
                if (toggleCompany) {
                  handleCompanyToggle(toggleCompany._id, toggleCompany.isActive);
                  setToggleConfirmDialogOpen(false);
                  setToggleCompany(null);
                }
              }}
              sx={{
                flex: 1,
                py: 1.5,
                borderRadius: 2,
                backgroundColor: theme.palette.mode === 'dark' ? '#ffffff' : '#1B4672',
                color: theme.palette.mode === 'dark' ? '#1B4672' : '#ffffff',
                fontSize: '1rem',
                fontWeight: 600,
                textTransform: 'none',
                fontFamily: '"Nunito Sans", sans-serif',
                '&:hover': {
                  backgroundColor: theme.palette.mode === 'dark' ? '#f0f0f0' : '#1B4672',
                  transform: 'translateY(-1px)',
                },
                transition: 'all 0.3s ease-in-out',
              }}
            >
              {toggleCompany?.isActive ? 'Disable' : 'Enable'}
            </Button>
          </Box>
        </DialogContent>
      </Dialog>

      {/* Success Dialog */}
      {/* Mobile Menu */}
      <Menu
        anchorEl={mobileMenuAnchor}
        open={Boolean(mobileMenuAnchor)}
        onClose={handleMobileMenuClose}
        anchorOrigin={{
          vertical: 'bottom',
          horizontal: 'right',
        }}
        transformOrigin={{
          vertical: 'top',
          horizontal: 'right',
        }}
        PaperProps={{
          sx: {
            backgroundColor: theme.palette.background.paper,
            color: theme.palette.text.primary,
            borderRadius: 2,
            minWidth: 200,
            marginLeft: '16px', // Push menu 30px to the left
          }
        }}
      >
        <MenuItem onClick={() => {
          if (selectedCompanyForMenu) {
            handleEditCompany(selectedCompanyForMenu);
          }
          handleMobileMenuClose();
        }}>
          <ListItemIcon>
            <svg width="16" height="16" viewBox="0 0 18 18" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M9.69659 10.4247L7.22192 10.7787L7.57526 8.30334L13.9393 1.93934C14.525 1.35355 15.4748 1.35355 16.0606 1.93934C16.6464 2.52513 16.6464 3.47489 16.0606 4.06068L9.69659 10.4247Z" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M13.2319 2.64648L15.3533 4.76782" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M13.5 10.5V15.5C13.5 16.0523 13.0523 16.5 12.5 16.5H2.5C1.94772 16.5 1.5 16.0523 1.5 15.5V5.5C1.5 4.94772 1.94772 4.5 2.5 4.5H7.5" stroke="currentColor" strokeWidth="1.2"/>
            </svg>
          </ListItemIcon>
          <ListItemText>Edit Company</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          if (selectedCompanyForMenu) {
            setSelectedCompany(selectedCompanyForMenu);
            setCreateUserDialogOpen(true);
          }
          handleMobileMenuClose();
        }}>
          <ListItemIcon>
            <svg width="16" height="16" viewBox="0 0 21 14" fill="none">
              <path d="M15.9545 8.75V6.125H13.2273V4.375H15.9545V1.75H17.7727V4.375H20.5V6.125H17.7727V8.75H15.9545ZM7.77273 7C6.77273 7 5.91667 6.65729 5.20455 5.97187C4.49242 5.28646 4.13636 4.4625 4.13636 3.5C4.13636 2.5375 4.49242 1.71354 5.20455 1.02813C5.91667 0.342708 6.77273 0 7.77273 0C8.77273 0 9.62879 0.342708 10.3409 1.02813C11.053 1.71354 11.4091 2.5375 11.4091 3.5C11.4091 4.4625 11.053 5.28646 10.3409 5.97187C9.62879 6.65729 8.77273 7 7.77273 7ZM0.5 14V11.55C0.5 11.0542 0.632727 10.5986 0.898182 10.1832C1.16364 9.76792 1.51576 9.45058 1.95455 9.23125C2.89394 8.77917 3.84848 8.44025 4.81818 8.2145C5.78788 7.98875 6.77273 7.87558 7.77273 7.875C8.77273 7.87442 9.75758 7.98758 10.7273 8.2145C11.697 8.44142 12.6515 8.78033 13.5909 9.23125C14.0303 9.45 14.3827 9.76733 14.6482 10.1832C14.9136 10.5992 15.0461 11.0548 15.0455 11.55V14H0.5Z" fill="currentColor"/>
            </svg>
          </ListItemIcon>
          <ListItemText>Add New User</ListItemText>
        </MenuItem>
        
        <MenuItem onClick={() => {
          if (selectedCompanyForMenu) {
            setSelectedCompany(selectedCompanyForMenu);
            setDeleteTarget('company');
            setDeleteDialogOpen(true);
          }
          handleMobileMenuClose();
        }}>
          <ListItemIcon>
            <svg width="16" height="16" viewBox="0 0 18 16" fill="none">
              <path fillRule="evenodd" clipRule="evenodd" d="M13.5001 15.2012H5.10015C4.4374 15.2012 3.90015 14.6639 3.90015 14.0012V3.20117H14.7001V14.0012C14.7001 14.6639 14.1629 15.2012 13.5001 15.2012Z" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M7.5001 11.6008V6.80078" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M11.1 11.6008V6.80078" stroke="currentColor" strokeWidth="1.2"/>
              <path d="M1.5 3.20117H17.1" stroke="currentColor" strokeWidth="1.2"/>
              <path fillRule="evenodd" clipRule="evenodd" d="M11.1 0.800781H7.50005C6.83731 0.800781 6.30005 1.33804 6.30005 2.00078V3.20078H12.3V2.00078C12.3 1.33804 11.7628 0.800781 11.1 0.800781Z" stroke="currentColor" strokeWidth="1.2"/>
            </svg>
          </ListItemIcon>
          <ListItemText>Delete Company</ListItemText>
        </MenuItem>
        
        {/* Access Dashboard - Mobile Only */}
        {selectedCompanyForMenu?.users?.some(user => user.role === 'admin' && user.isActive) && (
          <MenuItem onClick={() => {
            if (selectedCompanyForMenu) {
              const adminUser = selectedCompanyForMenu.users?.find(user => user.role === 'admin' && user.isActive);
              if (adminUser) {
                handleAccessDashboard(adminUser);
              }
            }
            handleMobileMenuClose();
          }}>
            <ListItemIcon>
              <DashboardIcon />
            </ListItemIcon>
            <ListItemText>Access Dashboard</ListItemText>
          </MenuItem>
        )}
      </Menu>

      <SuccessDialog
        open={showSuccessDialog}
        onClose={() => setShowSuccessDialog(false)}
        title={successDialogConfig.title}
        message={successDialogConfig.message}
        buttonText="Got it"
        autoCloseDelay={successDialogConfig.autoCloseDelay}
      />
    </Box>
  );
};

export default ClientManagement;